import moment from 'moment-timezone';
import { AddNoteReminderModalContent } from './add-note-reminder-modal-content';
import {
  AddNoteReminderSheetContent,
  NoteReminderFormProps,
} from './add-note-reminder-sheet-content';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { Moment } from 'moment';
import { Platform } from 'react-native';
import { Position } from '@/contexts/toast-context';
import { useCallback, useState } from 'react';
import { useLayoutContext } from '@/contexts/layout-context';

const POPOVER_SHIFT = 40;

interface Props
  extends Pick<
    NoteReminderFormProps,
    'noteReminder' | 'onClose' | 'buttonRef' | 'onChange' | 'buttonRef'
  > {}

export const AddNoteReminderActionSheet: React.FC<Props> = ({
  onClose,
  onChange,
  noteReminder,
  buttonRef,
}) => {
  const earliestPossibleDay = moment().startOf('day');
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const [localReminder, setLocalReminder] = useState(noteReminder);

  const isSaveEnabled = () => {
    // for first time open, allow saving when reminder has both time and date
    if (!noteReminder) {
      return Boolean(localReminder?.date) && Boolean(localReminder?.time);
    }
    // Validated the way it disables SAVE when clicked other date and then the same again
    // for subsequent opens, the initial reminder is defined, so allow saving when:
    // 1. reminder is cleared (both date and time are undefined)
    // 2. reminder has both time and date and any of both are different from initial reminder
    return (
      (!Boolean(localReminder?.date) && !Boolean(localReminder?.time)) ||
      localReminder?.date !== noteReminder.date ||
      localReminder?.time !== noteReminder.time
    );
  };

  const onSaveClicked = () => {
    onChange(localReminder);
    onClose();
  };

  const onCloseClicked = () => {
    // clear local reminder state when closing
    setLocalReminder(undefined);
    onClose();
  };

  const isDateDisabled = useCallback(
    (date: Moment) => {
      return date.isBefore(earliestPossibleDay);
    },
    [earliestPossibleDay],
  );

  const showPopover = Boolean(
    !isSmallLayout && buttonRef && Platform.OS === 'web',
  );

  const noteReminderContentProps = {
    isDateDisabled,
    noteReminder: localReminder,
    onChange: setLocalReminder,
    onClose: onCloseClicked,
    onSave: onSaveClicked,
    onSaveEnabled: isSaveEnabled(),
  };

  return (
    <BaseActionSheet
      contentStyle={
        showPopover ? styles.popoverContainer : styles.sheetContainer
      }
      isVisible
      onClose={onClose}
      popoverStyle={
        showPopover
          ? (_, target) => ({
              left: target.x,
              top: target.y - POPOVER_SHIFT,
            })
          : undefined
      }
      popoverTarget={buttonRef?.current}
      toastPosition={!showPopover ? Position.TOP : undefined}
    >
      {showPopover ? (
        // Popover layout (Desktop)
        <AddNoteReminderModalContent {...noteReminderContentProps} />
      ) : (
        // Action Sheet layout (Mobile)
        <AddNoteReminderSheetContent {...noteReminderContentProps} />
      )}
    </BaseActionSheet>
  );
};

const useStyles = createStyles(() => ({
  popoverContainer: {
    maxHeight: 600,
    width: 400,
  },
  sheetContainer: {
    paddingTop: 16,
  },
}));
